import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { KeywordPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/shopify')({
  component: ShopifyPage,
  head: () => ({
    meta: [
      { title: 'Shopify Uptime Monitoring — Checkout Alerts' },
      { name: 'description', content: 'Monitor your Shopify store, checkout, API 24/7. 30s checks, SSL monitoring, keyword detection, status pages. From $3/mo.' },
      { property: 'og:title', content: 'Shopify Uptime Monitoring — Store & Checkout Alerts | Uptrack' },
      { property: 'og:description', content: 'Monitor your Shopify storefront, checkout, and API. 30s checks catch outages before customers leave. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-shopify.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/shopify' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Shopify', item: 'https://uptrack.app/for/shopify' },
          ],
        }),
      },
    ],
  }),
})

function ShopifyPage() {
  return (
    <FeaturePage
      eyebrow='For · Shopify stores'
      section='Shopify stores'
      title='Your Shopify store is your business. Monitor it.'
      intro='Shopify handles hosting, but outages still happen. Monitor your storefront, checkout flow, and API endpoints every 30 seconds. Know before your customers complain.'
      featuresHeading='Complete Shopify store monitoring'
      features={[
        { title: 'Storefront + checkout + API', desc: 'Monitor every critical path: your homepage, product pages, checkout flow, and Storefront API separately.' },
        { title: 'SSL monitoring', desc: "Your custom domain's SSL certificate matters for trust and SEO. Get alerted before it expires." },
        { title: 'Maintenance mode detection', desc: 'Keyword monitoring catches Shopify maintenance pages or "store unavailable" messages instantly.' },
        { title: 'Status page for customers', desc: 'Give customers a branded status page. Reduce "is your store down?" support messages during incidents.' },
        { title: '30-second checks', desc: 'Most monitors check every 5 minutes. With 30-second checks, you catch outages 10x faster than competitors.' },
        { title: 'Alert confirmation', desc: 'Uptrack re-verifies failures before alerting you. No false alarms from brief Shopify hiccups.' },
      ]}
      why={{ heading: `Don't trust "it just works"`, body: 'Shopify is reliable, but not immune to outages. App conflicts, theme updates, and third-party scripts can break your store. When checkout goes down during a flash sale, you need to know immediately — not when your revenue report shows a gap.' }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor your Shopify store, checkout, and API. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <KeywordPiece />
        </ProductFrame>
      }
    />
  )
}
